<script setup lang="ts">
import { ref, onMounted } from "vue";
import { trackPageView } from "@/analytics/app-analytics";
import { useQueryClient } from "@tanstack/vue-query";
import { useDrawingStore } from "@/stores/fileStore";
import { Logger } from "@/logger";
import router from "@/router";
import UserState from "@/stores/UserState";
import { useLogout } from "@/utils/logout";
import DRUserAvatar from "@/components/UI/DRUserAvatar.vue";
import UserRoleBadge from "@/components/admin/UserRoleBadge.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import UsageSummaryBlock from "@/components/layouts/navbar/UsageSummaryBlock.vue";
import { useTranslation } from "i18next-vue";
import { QueryKey } from "@/stores/queries/query-keys";

const { t } = useTranslation();

const queryClient = useQueryClient();
const offcanvasEl = ref<HTMLElement | null>(null);

onMounted(() => {
  if (offcanvasEl.value) {
    offcanvasEl.value.addEventListener("show.bs.offcanvas", () => {
      trackPageView("panel-account");
      queryClient.invalidateQueries({ queryKey: [QueryKey.LICENSE_SUMMARY] });
    });
  }
});

const drawingStore = useDrawingStore();
const { logout } = useLogout();

const logoutHandler = async () => {
  Logger.info(`OffCanvasAccountInfo.vue : logging user out`);
  if (!drawingStore.areAllSynced) {
    if (!window.confirm(t("logout-sync-warning"))) {
      Logger.info(`OffCanvasAccountInfo.vue : user cancels logging out`);
      return;
    } else {
      Logger.info(`OffCanvasAccountInfo.vue : user accepts to lose drawings`);
    }
  }

  logout();
  router.replace({ name: "welcome" });
};
</script>
<template>
  <a
    data-bs-toggle="offcanvas"
    href="#offcanvasAccountInfo"
    role="button"
    aria-controls="offcanvasHelp"
    class="dr-tooltip dr-tooltip--right"
    :data-tooltip="t('navbar.accountTooltip')"
    :aria-label="t('navbar.accountTooltip')"
  >
    <DRUserAvatar v-if="UserState.data" :user="UserState.data" :size="30" />
  </a>

  <div
    ref="offcanvasEl"
    class="offcanvas offcanvas-end account-offcanvas"
    tabindex="-1"
    id="offcanvasAccountInfo"
    aria-labelledby="offcanvasAccountInfoLabel"
  >
    <div class="offcanvas-header border-bottom">
      <h5 class="offcanvas-title fw-semibold" id="offcanvasAccountInfoLabel">
        {{ t("account") }}
      </h5>
      <button
        type="button"
        class="btn-close"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      ></button>
    </div>

    <div class="offcanvas-body d-flex flex-column gap-3 p-3">
      <!-- User profile -->
      <div v-if="UserState.data" class="account-profile">
        <DRUserAvatar :user="UserState.data" :size="64" />
        <div class="account-profile__name">
          {{ UserState.data.full_name }}
        </div>
        <div class="account-profile__email">
          {{ UserState.data.email }}
        </div>
        <div class="account-profile__meta">
          <span
            v-if="UserState.data.organization?.name"
            class="account-profile__org"
          >
            <PhosphorIcon name="buildings" :size="16" weight="fill" />
            {{ UserState.data.organization.name }}
          </span>
          <UserRoleBadge :role="UserState.data.role" />
        </div>
      </div>

      <!-- Subscription & usage -->
      <UsageSummaryBlock />

      <div class="flex-grow-1"></div>

      <!-- Logout -->
      <div class="pt-3 border-top">
        <DRBtn
          class="w-100"
          :label="t('logout')"
          variant="outline-danger"
          icon="bi-box-arrow-right"
          @click="logoutHandler"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.account-offcanvas {
  background-color: var(--bs-body-bg);
}

.account-profile {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 1rem 1rem;
  text-align: center;

  &__name {
    margin-top: 0.75rem;
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--bs-body-color);
  }

  &__email {
    font-size: 0.825rem;
    color: var(--bs-secondary-color);
    margin-top: 0.125rem;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  &__org {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8rem;
    color: var(--bs-white);
    background-color: var(--bs-dark);
    padding: 0rem 0.85rem;
    height: 24px;
    border-radius: 4px;
  }
}
</style>
